import { PredefinedAppointment as MyleModelsResponseCalendarPredefinedAppointment } from "@/Appointments/Api/Dto/Responses/PredefinedAppointment";
import { EnumRecurrenceType } from "@/Appointments/Models/EnumRecurrenceType";
import { RecurrenceType } from "@/Appointments/Models/RecurrenceType";
import { Appointment as MyleModelsResponseCalendarAppointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { Room } from "@/Common/Api/Appointment/Dto/Room";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { ImportableAppointmentPatientSnapshot } from "@/Common/Models/Appointments/AppointmentPatientSnapshot";
import { Guid } from "@/Common/Models/Guid";
import { PatientLight } from "@/Common/Models/Patient/PatientLight";
import { ImportableThirdPartyPatient } from "@/Common/Models/Patient/ThirdPartyPatient";
import { Person } from "@/Common/Models/Person/Person";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";

type Appointment = "Appointment";
type PredefinedAppointment = "PredefinedAppointment";
type SelectedPatientId = "SelectedPatientId";
type SelectedPatient = "SelectedPatient";
type PatientSearchString = "PatientSearchString";
type PatientFamilyDoctorId = "PatientFamilyDoctorId";
type PatientFamilyDoctor = "PatientFamilyDoctor";
type Subject = "Subject";
type DefaultSubjects = "DefaultSubjects";
type SelectedSubjectService = "SelectedSubjectService";
type SelectedSubjectServiceId = "SelectedSubjectServiceId";
type SelectedSubjectResourceDefinitionId = "SelectedSubjectResourceDefinitionId";
type SelectedRoomId = "SelectedRoomId";
type Rooms = "Rooms";
type RoomsDisplayText = "RoomsDisplayText";
type RoomText = "RoomText";
type SelectedSiteId = "SelectedSiteId";
type Sites = "Sites";
type StartTime = "StartTime";
type EndTime = "EndTime";
type IsDateInvalid = "IsDateInvalid";
type SelectedIntervenors = "SelectedIntervenors";
type Intervenors = "Intervenors";
type AppointmentIntervenor = "AppointmentIntervenor";
type SelectedAppointmentStateCode = "SelectedAppointmentStateCode";
type AppointmentStates = "AppointmentStates";
type Notes = "Notes";
type PreviousNotes = "PreviousNotes";
type ReferralDoctorId = "ReferralDoctorId";
type ReferralOrganizationRuleId = "ReferralOrganizationRuleId";
type ReferralDoctors = "ReferralDoctors";
type ReferralDoctorSearchString = "ReferralDoctorSearchString";
type ReferralPhoneNumber = "ReferralPhoneNumber";
type SelectedRecurrenceType = "SelectedRecurrenceType";
type RecurrenceTypes = "RecurrenceTypes";
type RecurrenceEndDate = "RecurrenceEndDate";
type IsRecallsVisible = "IsRecallsVisible";
type IsPrintLabelOnSave = "IsPrintLabelOnSave";
type IsPrintCertificateOnSave = "IsPrintCertificateOnSave";
type IsNewAppointment = "IsNewAppointment";
type IsFromPredefinedAppointment = "IsFromPredefinedAppointment";
type IsFromCutAppointment = "IsFromCutAppointment";
type DurationForCutAndPaste = "DurationForCutAndPaste";
type RecreateUnderlyingPredefinedAppointment = "RecreateUnderlyingPredefinedAppointment";
type IsPaid = "IsPaid";
type IsNewPatientCreationMode = "IsNewPatientCreationMode";
type IsLinkPatientPortalMode = "IsLinkPatientPortalMode";
type IsLoadingPatientProfileFromPortal = "IsLoadingPatientProfileFromPortal";
type NotifyPatient = "NotifyPatient";
type NotifyPatientManualOverride = "NotifyPatientManualOverride";
type NotifyPatientDisabled = "NotifyPatientDisabled";
type IsTeleconsultation = "IsTeleconsultation";
type ContactPointNotificationList = "ContactPointNotificationList";
type HasNotificationContactPointSpecified = "HasNotificationContactPointSpecified";
type IsCalendarEventTab = "IsCalendarEventTab";

type Questionnaire = "Questionnaire";
type ShowQuestionnaire = "ShowQuestionnaire";

type OriginalPatientContactId = "OriginalPatientContactId";
type OriginalQuestionnaire = "OriginalQuestionnaire";
type OriginalStartTime = "OriginalStartTime";

type IsLoadingThirdPartyPatientData = "IsLoadingThirdPartyPatientData";
type IsLinkThirdPartyPatientMode = "IsLinkThirdPartyPatientMode";
type ThirdPartyPatient = "ThirdPartyPatient";
type PatientSnapshot = "PatientSnapshot";

type SendToAllPersonalContacts = "SendToAllPersonalContacts";
type SelectedCanadianTriageAndAcuityScore = "SelectedCanadianTriageAndAcuityScore";

interface IAppointmentEditModel {
    get(key: Appointment): MyleModelsResponseCalendarAppointment;
    set(key: Appointment, value: MyleModelsResponseCalendarAppointment): this;
    delete(key: Appointment): this;

    get(key: PredefinedAppointment): MyleModelsResponseCalendarPredefinedAppointment;
    set(key: PredefinedAppointment, value: MyleModelsResponseCalendarPredefinedAppointment): this;

    get(key: SelectedPatientId): Guid;
    set(key: SelectedPatientId, value: Guid): this;

    get(key: PatientSearchString): string;
    set(key: PatientSearchString, value: string): this;

    get(key: SelectedPatient): Immutable.List<Person>;
    set(key: SelectedPatient, value: Immutable.List<Person>): this;

    get(key: PatientFamilyDoctorId): string;
    set(key: PatientFamilyDoctorId, value: string): this;

    get(key: PatientFamilyDoctor): PatientLight;
    set(key: PatientFamilyDoctor, value: PatientLight): this;

    get(key: Subject): string;
    set(key: Subject, value: string): this;

    get(key: DefaultSubjects): ServiceModel[];
    set(key: DefaultSubjects, value: ServiceModel[]): this;

    get(key: SelectedSubjectService): ServiceModel;
    set(key: SelectedSubjectService, value: ServiceModel): this;

    get(key: SelectedSubjectServiceId): string;
    set(key: SelectedSubjectServiceId, value: string): this;

    get(key: SelectedSubjectResourceDefinitionId): string;
    set(key: SelectedSubjectResourceDefinitionId, value: string): this;

    get(key: SelectedSiteId): Guid;
    set(key: SelectedSiteId, value: Guid): this;

    get(key: Sites): Immutable.List<SitesModel>;
    set(key: Sites, value: Immutable.List<SitesModel>): this;

    get(key: Rooms): Immutable.List<Room>;
    set(key: Rooms, value: Immutable.List<Room>): this;

    get(key: RoomsDisplayText): Immutable.List<string>;
    set(key: RoomsDisplayText, value: Immutable.List<string>): this;

    get(key: SelectedRoomId): string;
    set(key: SelectedRoomId, value: string): this;

    get(key: RoomText): string;
    set(key: RoomText, value: string): this;

    get(key: StartTime): Date;
    set(key: StartTime, value: Date): this;

    get(key: EndTime): Date;
    set(key: EndTime, value: Date): this;

    get(key: IsDateInvalid): boolean;
    set(key: IsDateInvalid, value: boolean): this;

    get(key: SelectedAppointmentStateCode): string;
    set(key: SelectedAppointmentStateCode, value: string): this;

    get(key: AppointmentStates): Immutable.List<FlatLookupView>;
    set(key: AppointmentStates, value: Immutable.List<FlatLookupView>): this;

    get(key: SelectedIntervenors): Immutable.List<Person>;
    set(key: SelectedIntervenors, value: Immutable.List<Person>): this;

    get(key: Intervenors): Immutable.List<Person>;
    set(key: Intervenors, value: Immutable.List<Person>): this;

    get(key: AppointmentIntervenor): Person;
    set(key: AppointmentIntervenor, value: Person): this;

    get(key: Notes): string;
    set(key: Notes, value: string): this;

    get(key: PreviousNotes): string;
    set(key: PreviousNotes, value: string): this;

    get(key: SelectedRecurrenceType): EnumRecurrenceType;
    set(key: SelectedRecurrenceType, value: EnumRecurrenceType): this;

    get(key: RecurrenceTypes): Immutable.List<RecurrenceType>;
    set(key: RecurrenceTypes, value: Immutable.List<RecurrenceType>): this;

    get(key: RecurrenceEndDate): Date;
    set(key: RecurrenceEndDate, value: Date): this;

    get(key: IsRecallsVisible): boolean;
    set(key: IsRecallsVisible, value: boolean): this;

    get(key: IsPrintLabelOnSave): boolean;
    set(key: IsPrintLabelOnSave, value: boolean): this;

    get(key: IsPrintCertificateOnSave): boolean;
    set(key: IsPrintCertificateOnSave, value: boolean): this;

    get(key: IsNewAppointment): boolean;
    set(key: IsNewAppointment, value: boolean): this;

    get(key: IsFromPredefinedAppointment): boolean;
    set(key: IsFromPredefinedAppointment, value: boolean): this;

    get(key: IsFromCutAppointment): boolean;
    set(key: IsFromCutAppointment, value: boolean): this;

    get(key: ReferralDoctorId): string;
    set(key: ReferralDoctorId, value: string): this;

    get(key: ReferralOrganizationRuleId): string;
    set(key: ReferralOrganizationRuleId, value: string): this;

    get(key: ReferralDoctorSearchString): string;
    set(key: ReferralDoctorSearchString, value: string): this;

    get(key: ReferralDoctors): Immutable.List<Person>;
    set(key: ReferralDoctors, value: Immutable.List<Person>): this;

    get(key: ReferralPhoneNumber): string;
    set(key: ReferralPhoneNumber, value: string): this;

    get(key: DurationForCutAndPaste): number;
    set(key: DurationForCutAndPaste, value: number): this;

    get(key: RecreateUnderlyingPredefinedAppointment): boolean;
    set(key: RecreateUnderlyingPredefinedAppointment, value: boolean): this;

    get(key: IsPaid): boolean;
    set(key: IsPaid, value: boolean): this;

    get(key: IsNewPatientCreationMode): boolean;
    set(key: IsNewPatientCreationMode, value: boolean): this;

    get(key: IsLinkPatientPortalMode): boolean;
    set(key: IsLinkPatientPortalMode, value: boolean): this;

    get(key: IsLoadingPatientProfileFromPortal): boolean;
    set(key: IsLoadingPatientProfileFromPortal, value: boolean): this;

    get(key: NotifyPatient): boolean;
    set(key: NotifyPatient, value: boolean): this;

    get(key: NotifyPatientManualOverride): boolean;
    set(key: NotifyPatientManualOverride, value: boolean): this;

    get(key: NotifyPatientDisabled): boolean;
    set(key: NotifyPatientDisabled, value: boolean): this;

    get(key: IsTeleconsultation): boolean;
    set(key: IsTeleconsultation, value: boolean): this;

    get(key: ContactPointNotificationList): Immutable.List<string>;
    set(key: ContactPointNotificationList, value: Immutable.List<string>): this;

    get(key: HasNotificationContactPointSpecified): boolean;
    set(key: HasNotificationContactPointSpecified, value: boolean): this;

    get(key: Questionnaire): QuestionnaireStatusInfo;
    set(key: Questionnaire, value: QuestionnaireStatusInfo): this;

    get(key: ShowQuestionnaire): boolean;
    set(key: ShowQuestionnaire, value: boolean): this;

    get(key: OriginalPatientContactId): string;
    set(key: OriginalPatientContactId, value: string): this;

    get(key: OriginalQuestionnaire): QuestionnaireStatusInfo;
    set(key: OriginalQuestionnaire, value: QuestionnaireStatusInfo): this;

    get(key: OriginalStartTime): Date;
    set(key: OriginalStartTime, value: Date): this;

    get(key: IsLoadingThirdPartyPatientData): boolean;
    set(key: IsLoadingThirdPartyPatientData, value: boolean): this;

    get(key: IsLinkThirdPartyPatientMode): boolean;
    set(key: IsLinkThirdPartyPatientMode, value: boolean): this;

    get(key: ThirdPartyPatient): ImportableThirdPartyPatient;
    set(key: ThirdPartyPatient, value: ImportableThirdPartyPatient): this;

    get(key: PatientSnapshot): ImportableAppointmentPatientSnapshot;
    set(key: PatientSnapshot, value: ImportableAppointmentPatientSnapshot): this;

    get(key: SendToAllPersonalContacts): boolean;
    set(ket: SendToAllPersonalContacts, value: boolean): this;

    get(key: SelectedCanadianTriageAndAcuityScore): number;
    set(ket: SelectedCanadianTriageAndAcuityScore, value: number): this;

    get(key: IsCalendarEventTab): boolean;
    set(ket: IsCalendarEventTab, value: boolean): this;
}

class AppointmentEditModel {
    public static hasCoreAppointmentDataChanged(appointmentEditModel: IAppointmentEditModel): boolean {
        const appointment = appointmentEditModel.get("Appointment");

        if (!appointment.AppointmentStateLookupCode) {
            return AppointmentEditModel.wasAppointmentInfoAdded(appointmentEditModel);
        }

        return AppointmentEditModel.hasAppointmentPatientChanged(appointmentEditModel) ||
            AppointmentEditModel.hasAppointmentServiceChanged(appointmentEditModel) ||
            AppointmentEditModel.hasAppointmentSubjectChanged(appointmentEditModel) ||
            AppointmentEditModel.hasAppointmentStatusChanged(appointmentEditModel);
    }

    public static hasAppointmentPatientChanged(appointmentEditModel: IAppointmentEditModel): boolean {
        const appointment = appointmentEditModel.get("Appointment");
        const selectedPatientId = appointmentEditModel.get("SelectedPatientId");

        return appointment.PatientId !== selectedPatientId;
    }

    public static hasAppointmentServiceChanged(appointmentEditModel: IAppointmentEditModel): boolean {
        const appointment = appointmentEditModel.get("Appointment");
        const serviceId = appointmentEditModel.get("SelectedSubjectServiceId");

        return (IsNullOrWhiteSpace(serviceId) && !appointment.IsAdhocService) ||
            (!IsNullOrWhiteSpace(serviceId) && appointment.FilteredResourceId !== serviceId);
    }

    public static hasAppointmentSubjectChanged(appointmentEditModel: IAppointmentEditModel): boolean {
        return appointmentEditModel.get("Appointment").Subject !== appointmentEditModel.get("Subject");
    }

    public static hasAppointmentStatusChanged(appointmentEditModel: IAppointmentEditModel): boolean {
        const appointment = appointmentEditModel.get("Appointment");
        const stateCode = appointmentEditModel.get("SelectedAppointmentStateCode");

        return appointment.AppointmentStateLookupCode !== stateCode;
    }

    public static wasAppointmentInfoAdded(appointmentEditModel: IAppointmentEditModel): boolean {
        return !IsNullOrWhiteSpace(appointmentEditModel.get("SelectedPatientId")) ||
            !IsNullOrWhiteSpace(appointmentEditModel.get("SelectedSubjectServiceId")) ||
            !IsNullOrWhiteSpace(appointmentEditModel.get("Subject")) ||
            !AppointmentEditModel.isAppointmentUnconfirmed(appointmentEditModel);
    }

    public static isAppointmentUnconfirmed(appointmentEditModel: IAppointmentEditModel): boolean {
        return appointmentEditModel.get("SelectedAppointmentStateCode") === "UNCONFIRMED";
    }
}

export { AppointmentEditModel, IAppointmentEditModel };
